import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Column } from 'react-base-table';

import { fetchData } from '@store/errorLogicConfig/configData/actions';
import { setConfigSorting } from '@store/errorLogicConfig/base/actions';
import {
  configDataSortSelector,
  loadingSelector,
} from '@store/errorLogicConfig/configData/selectors';
import { Retry } from '@components/common';

import Summary from '../Common/Summary';
import { ContentWrapper, DataTableWrapper } from '../styled';
import { configTableDataColumns, filterConfigTableData, getRowCalss } from './utils';
import ConfigRowActions from './ConfigRowActions';
import WarningModal from './WarningModal';
import { ConfigColumnSearchContext } from './columnSearchContext';

import Table from '@components/common/Table';

/** Single-line header: title + search icon (popover opens fixed-width search input). */
const CONFIG_TABLE_HEADER_HEIGHT = 50;

function ConfigTable() {
  const dispatch = useDispatch();
  const [columnSearchTexts, setColumnSearchTexts] = useState<Record<string, string>>({});

  const { data, sort } = useSelector(configDataSortSelector);
  const { loading, showError } = useSelector(loadingSelector);

  const setColumnSearchText = useCallback((dataKey: string, value: string) => {
    setColumnSearchTexts((prev) => ({ ...prev, [dataKey]: value }));
  }, []);

  const filteredData = useMemo(
    () => filterConfigTableData(data, columnSearchTexts),
    [data, columnSearchTexts],
  );

  useEffect(() => {
    dispatch(fetchData());
  }, []);

  const onColumnSort = ({ key, order }) => {
    dispatch(setConfigSorting({ key, order }));
  };

  const menuColumn = useMemo(
    () => ({
      title: '',
      dataKey: '',
      key: 'action',
      width: 120,
      frozen: Column.FrozenDirection.RIGHT,
      headerRenderer: () => <div style={{ minHeight: CONFIG_TABLE_HEADER_HEIGHT }} />,
      cellRenderer: ({ rowData }) => <ConfigRowActions rowData={rowData} />,
    }),
    [],
  );

  const columnSearchContextValue = useMemo(
    () => ({
      columnSearchTexts,
      setColumnSearchText,
    }),
    [columnSearchTexts, setColumnSearchText],
  );

  const ColumnRenderer = useMemo(() => [...configTableDataColumns, menuColumn], [menuColumn]);

  const retryAction = () => dispatch(fetchData());
  return (
    <ContentWrapper>
      <Summary />
      <WarningModal />
      <DataTableWrapper>
        <ConfigColumnSearchContext.Provider value={columnSearchContextValue}>
          <Retry isLoading={loading} showError={showError} fetchData={retryAction}>
            <Table
              fixed
              headerHeight={CONFIG_TABLE_HEADER_HEIGHT}
              data={filteredData}
              columns={ColumnRenderer}
              onColumnSort={onColumnSort}
              rowClassName={getRowCalss}
              sortBy={sort}
            />
          </Retry>
        </ConfigColumnSearchContext.Provider>
      </DataTableWrapper>
    </ContentWrapper>
  );
}

export default ConfigTable;
